import type { Dayjs } from "dayjs";
import dayjs from "dayjs";
import type { IFormattedFitData } from "@/utils/wearableFitFormat";
import {
  buildFitnessDateKeysInRange,
  formatarDadosGoogleHealth,
} from "@/utils/wearableFitFormat";
import type { IGoogleHealthFitnessBundle } from "@/services/google/health";

/** Identificador fixo do paciente no servidor FHIR (mesmo padrão de AuxiliarObservation). */
const PATIENT_OID = "2.16.840.1.113883.13.237";

/**
 * Observation.method (CodeableConcept): origem wearable.
 * Usado na montagem das observations e reforçado no POST (PostWearableObservation).
 */
export const WEARABLE_OBSERVATION_METHOD = {
  coding: [
    {
      system: "https://fluxmed.com.br/fhir/CodeSystem/observation-method",
      code: "wearable",
      display: "Wearable Device",
    },
  ],
};

/**
 * UCUM para contagem de passos (LOINC 55423-8 etc.).
 * O código deve ser literalmente `{steps}` — não usar a string "steps" sem chaves no valueQuantity.
 */
export const UCUM_STEPS = "{steps}" as const;

/** Alinhado às 8 métricas exibidas no modal (Google Health aggregate). */
export type WearableShareKey =
  | "steps"
  | "calories"
  | "distance"
  | "heartRate"
  | "weight"
  | "activeMinutes"
  | "glucose"
  | "bloodPressure"
  | "sleep";

export type WearableCategoryCode = "activity" | "vital-signs" | "laboratory";

export interface WearableLoincDefinition {
  key: WearableShareKey;
  loinc: string;
  /** Rótulo alinhado aos cards do modal. */
  text: string;
  /** display do coding LOINC */
  display: string;
  categoryCode: WearableCategoryCode;
}

/**
 * Uma entrada por métrica do modal, mesma ordem de METRIC_CONFIG.
 * LOINCs: passos/calorias/sono/FC/PA conforme tabela wearable; distância e minutos ativos
 * com códigos usuais de atividade; glicose como laboratório.
 */
export const WEARABLE_LOINC_LIST: WearableLoincDefinition[] = [
  {
    key: "steps",
    loinc: "55423-8",
    text: "Passos",
    display: "Number of steps in 24 hour Measured",
    categoryCode: "activity",
  },
  {
    key: "calories",
    loinc: "9052-2",
    text: "Calorias",
    display: "Calories consumed",
    categoryCode: "activity",
  },
  {
    key: "distance",
    loinc: "13184-2",
    text: "Distância",
    display: "Distance walked",
    categoryCode: "activity",
  },
  {
    key: "heartRate",
    loinc: "8867-4",
    text: "Frequência cardíaca",
    display: "Heart rate",
    categoryCode: "vital-signs",
  },
  {
    key: "weight",
    loinc: "29463-7",
    text: "Peso",
    display: "Body weight",
    categoryCode: "vital-signs",
  },
  {
    key: "activeMinutes",
    loinc: "68516-4",
    text: "Minutos ativos",
    display: "Duration of physical activity",
    categoryCode: "activity",
  },
  {
    key: "glucose",
    loinc: "2339-0",
    text: "Glicose no sangue",
    display: "Glucose [Mass/volume] in Serum or Plasma",
    categoryCode: "laboratory",
  },
  {
    key: "bloodPressure",
    loinc: "85354-9",
    text: "Pressão arterial",
    display: "Blood pressure panel with all children optional",
    categoryCode: "vital-signs",
  },
  {
    key: "sleep",
    loinc: "93832-4",
    text: "Sono",
    display: "Sleep duration",
    categoryCode: "activity",
  },
];

function patientReference(patientId: string) {
  return `Patient/${PATIENT_OID}-${patientId}`;
}

function categoryDisplay(code: WearableCategoryCode): string {
  switch (code) {
    case "activity":
      return "Activity";
    case "vital-signs":
      return "Vital Signs";
    case "laboratory":
      return "Laboratory";
    default:
      return "Activity";
  }
}

function category(code: WearableCategoryCode, display: string) {
  return [
    {
      coding: [
        {
          system: "http://terminology.hl7.org/CodeSystem/observation-category",
          code,
          display,
        },
      ],
    },
  ];
}

function baseObservation(params: {
  patientId: string;
  patientDisplay: string;
  effectiveIso: string;
  issuedIso: string;
  loinc: string;
  text: string;
  display: string;
  categoryCode: WearableCategoryCode;
}) {
  const ref = patientReference(params.patientId);
  const catDisplay = categoryDisplay(params.categoryCode);
  return {
    resourceType: "Observation",
    status: "final",
    category: category(params.categoryCode, catDisplay),
    code: {
      coding: [
        {
          system: "http://loinc.org",
          code: params.loinc,
          display: params.display,
        },
      ],
      text: params.text,
    },
    subject: {
      reference: ref,
      display: params.patientDisplay,
    },
    effectiveDateTime: params.effectiveIso,
    issued: params.issuedIso,
    performer: [{ reference: ref }],
    method: WEARABLE_OBSERVATION_METHOD,
  };
}

/**
 * Data “do registro”: início do dia selecionado (agregação diária do Fit).
 */
export function effectiveDateTimeForWearableDay(selectedDate: Dayjs): string {
  return selectedDate.startOf("day").toISOString();
}

/** Indica se o aggregate retornou valor utilizável para envio FHIR. */
export function hasWearableValueForKey(
  fit: IFormattedFitData,
  key: WearableShareKey
): boolean {
  switch (key) {
    case "steps":
      return fit.passos > 0;
    case "calories":
      return fit.calorias > 0;
    case "distance":
      return fit.distanciaMetros > 0;
    case "heartRate":
      return fit.bpmMedio > 0;
    case "weight":
      return fit.pesoKg > 0;
    case "activeMinutes":
      return fit.minutosAtivos > 0;
    case "glucose":
      return fit.glicose > 0;
    case "bloodPressure":
      return fit.pressaoSistolica > 0 || fit.pressaoDiastolica > 0;
    case "sleep":
      return fit.sonoMinutos > 0;
    default:
      return false;
  }
}

export function buildWearableObservations(params: {
  fit: IFormattedFitData;
  selectedDate: Dayjs;
  patientId: string;
  patientDisplay: string;
  keys: WearableShareKey[];
}): Record<string, unknown>[] {
  const issued = new Date().toISOString();
  const effective = effectiveDateTimeForWearableDay(params.selectedDate);
  const out: Record<string, unknown>[] = [];

  for (const def of WEARABLE_LOINC_LIST) {
    if (!params.keys.includes(def.key)) continue;
    if (!hasWearableValueForKey(params.fit, def.key)) continue;

    const base = baseObservation({
      patientId: params.patientId,
      patientDisplay: params.patientDisplay,
      effectiveIso: effective,
      issuedIso: issued,
      loinc: def.loinc,
      text: def.text,
      display: def.display,
      categoryCode: def.categoryCode,
    });

    switch (def.key) {
      case "steps":
        out.push({
          ...base,
          valueQuantity: {
            value: Math.round(params.fit.passos),
            unit: UCUM_STEPS,
            system: "http://unitsofmeasure.org",
            code: UCUM_STEPS,
          },
        });
        break;
      case "calories":
        out.push({
          ...base,
          valueQuantity: {
            value: params.fit.calorias,
            unit: "kcal",
            system: "http://unitsofmeasure.org",
            code: "kcal",
          },
        });
        break;
      case "distance":
        out.push({
          ...base,
          valueQuantity: {
            value: Math.round(params.fit.distanciaMetros),
            unit: "m",
            system: "http://unitsofmeasure.org",
            code: "m",
          },
        });
        break;
      case "heartRate":
        out.push({
          ...base,
          valueQuantity: {
            value: params.fit.bpmMedio,
            unit: "/min",
            system: "http://unitsofmeasure.org",
            code: "/min",
          },
        });
        break;
      case "weight":
        out.push({
          ...base,
          valueQuantity: {
            value: params.fit.pesoKg,
            unit: "kg",
            system: "http://unitsofmeasure.org",
            code: "kg",
          },
        });
        break;
      case "activeMinutes":
        out.push({
          ...base,
          valueQuantity: {
            value: Math.round(params.fit.minutosAtivos * 10) / 10,
            unit: "min",
            system: "http://unitsofmeasure.org",
            code: "min",
          },
        });
        break;
      case "glucose":
        out.push({
          ...base,
          valueQuantity: {
            value: params.fit.glicose,
            unit: "mg/dL",
            system: "http://unitsofmeasure.org",
            code: "mg/dL",
          },
        });
        break;
      case "bloodPressure": {
        const sys = params.fit.pressaoSistolica;
        const dia = params.fit.pressaoDiastolica;
        out.push({
          ...base,
          component: [
            {
              code: {
                coding: [
                  {
                    system: "http://loinc.org",
                    code: "8480-6",
                    display: "Systolic blood pressure",
                  },
                ],
                text: "Sistólica",
              },
              valueQuantity: {
                value: sys,
                unit: "mm[Hg]",
                system: "http://unitsofmeasure.org",
                code: "mm[Hg]",
              },
            },
            {
              code: {
                coding: [
                  {
                    system: "http://loinc.org",
                    code: "8462-4",
                    display: "Diastolic blood pressure",
                  },
                ],
                text: "Diastólica",
              },
              valueQuantity: {
                value: dia,
                unit: "mm[Hg]",
                system: "http://unitsofmeasure.org",
                code: "mm[Hg]",
              },
            },
          ],
        });
        break;
      }
      case "sleep":
        out.push({
          ...base,
          valueQuantity: {
            value: Math.round(params.fit.sonoMinutos * 10) / 10,
            unit: "min",
            system: "http://unitsofmeasure.org",
            code: "min",
          },
        });
        break;
      default:
        break;
    }
  }

  return out;
}

/** Verifica se há valor em qualquer dia do período para a métrica. */
export function hasWearableValueForKeyInPeriod(
  bundle: IGoogleHealthFitnessBundle | null | undefined,
  periodStart: Dayjs,
  periodEnd: Dayjs,
  key: WearableShareKey
): boolean {
  if (!bundle) return false;
  for (const dateKey of buildFitnessDateKeysInRange(periodStart, periodEnd)) {
    const fit = formatarDadosGoogleHealth(bundle, dayjs(dateKey));
    if (hasWearableValueForKey(fit, key)) return true;
  }
  return false;
}

/**
 * Monta observations FHIR dia a dia no período (envio ao prontuário).
 * Cada dia com valor gera suas próprias observations.
 */
export function buildWearableObservationsForPeriod(params: {
  fitnessBundle: IGoogleHealthFitnessBundle;
  periodStart: Dayjs;
  periodEnd: Dayjs;
  patientId: string;
  patientDisplay: string;
  keys: WearableShareKey[];
}): Record<string, unknown>[] {
  const out: Record<string, unknown>[] = [];

  for (const dateKey of buildFitnessDateKeysInRange(
    params.periodStart,
    params.periodEnd
  )) {
    const day = dayjs(dateKey);
    const fit = formatarDadosGoogleHealth(params.fitnessBundle, day);
    out.push(
      ...buildWearableObservations({
        fit,
        selectedDate: day,
        patientId: params.patientId,
        patientDisplay: params.patientDisplay,
        keys: params.keys,
      })
    );
  }

  return out;
}
